/* ==========================================================================
   LYVORA V1 — global.js
   Web Components du thème. Zéro dépendance externe.
   Convention : tous les éléments sont déclaratifs (attributs data-*),
   les sections ne définissent JAMAIS leurs propres custom elements.
   ========================================================================== */
(function () {
  'use strict';

  const L = window.Lyvora || { routes: {}, settings: {}, strings: {} };
  window.Lyvora = L;

  /* --- Utilitaires ------------------------------------------------------- */
  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const onReady = (fn) => {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  };

  L.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || L.settings.moneyFormat || '{{amount}} €';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ' ';
      decimal = decimal || ',';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    let value = '';
    switch (fmt.match(placeholderRegex)[1]) {
      case 'amount': value = formatWithDelimiters(cents, 2, ',', '.'); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, ' ', ','); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return fmt.replace(placeholderRegex, value);
  };

  const trapFocus = (container) => {
    // Les focusables sont recalculés à chaque Tab : le contenu des panneaux
    // (quick-add, drawer panier re-rendu) est injecté dynamiquement.
    container.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      const focusables = container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  };

  /* --- Toasts ------------------------------------------------------------- */
  L.Toast = {
    show(message, type = 'success', duration = 3200) {
      const center = document.querySelector('.lv-toast-center');
      if (!center || !message) return;
      const toast = document.createElement('div');
      toast.className = `lv-toast lv-toast--${type}`;
      toast.setAttribute('role', 'status');
      toast.innerHTML =
        (type === 'success'
          ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="m9 11 3 3L22 4"/></svg>'
          : '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>') +
        `<span>${message}</span>`;
      center.appendChild(toast);
      setTimeout(() => {
        toast.classList.add('is-leaving');
        setTimeout(() => toast.remove(), 350);
      }, duration);
    },
  };
  window.LyvoraToast = L.Toast;

  /* --- Apparition au scroll ------------------------------------------------ */
  onReady(() => {
    if (!('IntersectionObserver' in window) || document.documentElement.classList.contains('no-reveal')) {
      document.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    const watch = (root) => root.querySelectorAll('.reveal:not(.is-revealed)').forEach((el) => io.observe(el));
    watch(document);
    new MutationObserver((muts) => {
      muts.forEach((m) =>
        m.addedNodes.forEach((n) => {
          if (n.nodeType === 1) watch(n);
        })
      );
    }).observe(document.body, { childList: true, subtree: true });
  });

  /* --- <lv-slider> ----------------------------------------------------------- */
  class LvSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.lv-slider__track');
      if (!this.track) return;
      this.style.setProperty('--per-view', this.dataset.perView || 3);
      this.style.setProperty('--per-view-mobile', this.dataset.perViewMobile || 1.15);
      if (this.dataset.gap) this.style.setProperty('--slider-gap', this.dataset.gap + 'px');

      this.controls = null;
      if (this.hasAttribute('data-arrows') || this.hasAttribute('data-dots')) this.buildControls();

      this.track.addEventListener('scroll', debounce(() => this.sync(), 80), { passive: true });
      window.addEventListener('resize', debounce(() => this.sync(), 150));

      const autoplay = parseInt(this.dataset.autoplay || '0', 10);
      if (autoplay > 0) {
        this.autoplayTimer = setInterval(() => {
          if (this.matches(':hover') || document.hidden) return;
          this.isAtEnd() ? this.scrollToPage(0) : this.next();
        }, autoplay);
      }
      this.sync();
    }

    disconnectedCallback() {
      clearInterval(this.autoplayTimer);
    }

    pageWidth() {
      return this.track.clientWidth;
    }
    pages() {
      return Math.max(1, Math.ceil((this.track.scrollWidth - this.track.clientWidth - 4) / this.pageWidth()) + 1);
    }
    currentPage() {
      return Math.round(this.track.scrollLeft / this.pageWidth());
    }
    isAtEnd() {
      return this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 8;
    }
    next() {
      if (this.isAtEnd() && this.hasAttribute('data-loop')) return this.scrollToPage(0);
      this.track.scrollBy({ left: this.pageWidth(), behavior: 'smooth' });
    }
    prev() {
      this.track.scrollBy({ left: -this.pageWidth(), behavior: 'smooth' });
    }
    scrollToPage(i) {
      this.track.scrollTo({ left: i * this.pageWidth(), behavior: 'smooth' });
    }

    buildControls() {
      this.controls = document.createElement('div');
      this.controls.className = 'lv-slider__controls';
      if (this.hasAttribute('data-arrows')) {
        this.prevBtn = this.makeArrow('prev');
        this.controls.appendChild(this.prevBtn);
      }
      if (this.hasAttribute('data-dots')) {
        this.dots = document.createElement('div');
        this.dots.className = 'lv-slider__dots';
        this.controls.appendChild(this.dots);
      }
      if (this.hasAttribute('data-arrows')) {
        this.nextBtn = this.makeArrow('next');
        this.controls.appendChild(this.nextBtn);
      }
      this.appendChild(this.controls);
    }

    makeArrow(dir) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'lv-slider__arrow';
      b.setAttribute('aria-label', dir === 'prev' ? 'Précédent' : 'Suivant');
      b.innerHTML =
        dir === 'prev'
          ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>'
          : '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>';
      b.addEventListener('click', () => (dir === 'prev' ? this.prev() : this.next()));
      return b;
    }

    sync() {
      const pages = this.pages();
      if (this.controls) this.controls.style.display = pages <= 1 ? 'none' : '';
      if (this.dots) {
        if (this.dots.children.length !== pages) {
          this.dots.innerHTML = '';
          for (let i = 0; i < pages; i++) {
            const d = document.createElement('button');
            d.type = 'button';
            d.className = 'lv-slider__dot';
            d.setAttribute('aria-label', `Aller à la page ${i + 1}`);
            d.addEventListener('click', () => this.scrollToPage(i));
            this.dots.appendChild(d);
          }
        }
        [...this.dots.children].forEach((d, i) => d.classList.toggle('is-active', i === this.currentPage()));
      }
      if (this.prevBtn && !this.hasAttribute('data-loop')) {
        this.prevBtn.toggleAttribute('disabled', this.track.scrollLeft <= 4);
        this.nextBtn.toggleAttribute('disabled', this.isAtEnd());
      }
    }
  }
  customElements.define('lv-slider', LvSlider);

  /* --- <lv-marquee> ------------------------------------------------------------ */
  class LvMarquee extends HTMLElement {
    connectedCallback() {
      this.row = this.querySelector('.lv-marquee__row');
      if (!this.row) return;
      // Jeu d'items original conservé pour les reconstructions (resize)
      this.originals = [...this.row.children].map((n) => n.cloneNode(true));
      const direction = this.dataset.direction === 'right' ? 'reverse' : 'normal';
      this.style.setProperty('--marquee-anim-direction', direction);
      this.rebuild = this.rebuild.bind(this);
      // Attend le layout (clientWidth fiable), puis construit ; reconstruit au resize
      requestAnimationFrame(this.rebuild);
      window.addEventListener('resize', debounce(this.rebuild, 250));
      if (this.hasAttribute('data-pause-hover')) {
        this.playback = 1;
        this.addEventListener('pointerenter', () => this.ease(0.12));
        this.addEventListener('pointerleave', () => this.ease(1));
      }
    }

    rebuild() {
      const width = this.clientWidth || window.innerWidth || 1400;
      if (this.builtFor === width) return;
      this.builtFor = width;
      const markDecorative = (el) => {
        el.setAttribute('aria-hidden', 'true');
        if (el.matches('a, button')) el.setAttribute('tabindex', '-1');
        el.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((f) =>
          f.setAttribute('tabindex', '-1')
        );
      };
      const appendSet = (decorative) => {
        this.originals.forEach((n) => {
          const clone = n.cloneNode(true);
          if (decorative) markDecorative(clone);
          this.row.appendChild(clone);
        });
      };
      // 1) Un set accessible, complété jusqu'à couvrir tout l'écran
      this.row.innerHTML = '';
      appendSet(false);
      let guard = 0;
      while (this.row.scrollWidth < width && guard < 20) {
        appendSet(true);
        guard++;
      }
      // 2) Duplication intégrale : l'animation boucle à -50 % sans jamais montrer de vide
      [...this.row.children].forEach((n) => {
        const clone = n.cloneNode(true);
        markDecorative(clone);
        this.row.appendChild(clone);
      });
      const speed = parseFloat(this.dataset.speed || '40'); // px/s
      const half = this.row.scrollWidth / 2;
      this.style.setProperty('--marquee-duration', `${Math.max(half / speed, 5)}s`);
    }

    ease(target) {
      cancelAnimationFrame(this.raf);
      const anim = this.row.getAnimations()[0];
      if (!anim) return;
      const step = () => {
        this.playback += (target - this.playback) * 0.08;
        if (Math.abs(target - this.playback) < 0.01) this.playback = target;
        anim.updatePlaybackRate(Math.max(this.playback, 0.05));
        if (this.playback !== target) this.raf = requestAnimationFrame(step);
      };
      this.raf = requestAnimationFrame(step);
    }
  }
  customElements.define('lv-marquee', LvMarquee);

  /* --- <lv-countdown> ------------------------------------------------------------ */
  class LvCountdown extends HTMLElement {
    connectedCallback() {
      const end = this.dataset.end;
      if (!end) return;
      this.endDate = new Date(end);
      if (isNaN(this.endDate)) return;
      this.els = {
        days: this.querySelector('[data-cd="days"]'),
        hours: this.querySelector('[data-cd="hours"]'),
        minutes: this.querySelector('[data-cd="minutes"]'),
        seconds: this.querySelector('[data-cd="seconds"]'),
      };
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
    }
    tick() {
      let diff = Math.max(0, Math.floor((this.endDate - new Date()) / 1000));
      if (diff <= 0) {
        clearInterval(this.timer);
        this.setAttribute('data-ended', '');
        const endedEl = this.querySelector('[data-cd-ended]');
        const unitsEl = this.querySelector('[data-cd-units]');
        if (endedEl) endedEl.hidden = false;
        if (unitsEl && endedEl) unitsEl.hidden = true;
      }
      const d = Math.floor(diff / 86400);
      const h = Math.floor((diff % 86400) / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      const pad = (n) => String(n).padStart(2, '0');
      if (this.els.days) this.els.days.textContent = pad(d);
      if (this.els.hours) this.els.hours.textContent = pad(h);
      if (this.els.minutes) this.els.minutes.textContent = pad(m);
      if (this.els.seconds) this.els.seconds.textContent = pad(s);
    }
  }
  customElements.define('lv-countdown', LvCountdown);

  /* --- <lv-tabs> -------------------------------------------------------------------- */
  class LvTabs extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-tab]');
        if (!btn || !this.contains(btn)) return;
        this.activate(btn.dataset.tab);
      });
      const first = this.querySelector('[data-tab]');
      const active = this.querySelector('[data-tab].is-active') || first;
      if (active) this.activate(active.dataset.tab);
    }
    activate(id) {
      this.querySelectorAll('[data-tab]').forEach((b) => {
        const on = b.dataset.tab === id;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', on);
      });
      this.querySelectorAll('[data-panel]').forEach((p) => {
        p.classList.toggle('is-active', p.dataset.panel === id);
      });
      this.dispatchEvent(new CustomEvent('lyvora:tab:change', { detail: { id }, bubbles: true }));
    }
  }
  customElements.define('lv-tabs', LvTabs);

  /* --- <lv-accordion-group> ------------------------------------------------------------ */
  class LvAccordionGroup extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const summary = e.target.closest('summary');
        if (!summary || !this.contains(summary)) return;
        const details = summary.parentElement;
        if (!details.matches('details')) return;
        e.preventDefault();
        details.open ? this.close(details) : this.open(details);
      });
    }
    open(details) {
      if (this.hasAttribute('data-single')) {
        this.querySelectorAll('details[open]').forEach((d) => {
          if (d !== details) this.close(d);
        });
      }
      details.open = true;
      const content = details.querySelector('.lv-accordion__content');
      if (!content) return;
      content.style.overflow = 'hidden';
      const anim = content.animate(
        { maxHeight: ['0px', content.scrollHeight + 'px'], opacity: [0, 1] },
        { duration: 260, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
      );
      anim.onfinish = () => (content.style.overflow = '');
    }
    close(details) {
      const content = details.querySelector('.lv-accordion__content');
      if (!content) {
        details.open = false;
        return;
      }
      content.style.overflow = 'hidden';
      const anim = content.animate(
        { maxHeight: [content.scrollHeight + 'px', '0px'], opacity: [1, 0] },
        { duration: 220, easing: 'ease-out' }
      );
      anim.onfinish = () => {
        details.open = false;
        content.style.overflow = '';
      };
    }
  }
  customElements.define('lv-accordion-group', LvAccordionGroup);

  /* --- <lv-drawer> / <lv-modal> --------------------------------------------------------- */
  class LvOverlayBase extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.lv-panel');
      this.overlay = this.querySelector('.lv-overlay');
      if (this.overlay) this.overlay.addEventListener('click', () => this.close());
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-close]')) this.close();
      });
      if (this.panel) trapFocus(this.panel);
    }
    open() {
      this.lastFocus = document.activeElement;
      this.classList.add('is-open');
      document.body.classList.add('lv-locked');
      this.dispatchEvent(new CustomEvent('lyvora:open', { bubbles: true }));
      requestAnimationFrame(() => {
        const target = this.panel && (this.panel.querySelector('[autofocus]') || this.panel.querySelector('input, button, a'));
        if (target) target.focus({ preventScroll: true });
      });
    }
    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('lv-locked');
      this.dispatchEvent(new CustomEvent('lyvora:close', { bubbles: true }));
      if (this.lastFocus) this.lastFocus.focus({ preventScroll: true });
    }
    get isOpen() {
      return this.classList.contains('is-open');
    }
  }
  customElements.define('lv-drawer', class extends LvOverlayBase {});
  customElements.define('lv-modal', class extends LvOverlayBase {});

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-open]');
    if (!trigger) return;
    const target = document.getElementById(trigger.dataset.open);
    if (target && typeof target.open === 'function') {
      e.preventDefault();
      target.open();
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('lv-drawer.is-open, lv-modal.is-open').forEach((d) => d.close());
  });

  /* --- <lv-header> -------------------------------------------------------------------------- */
  class LvHeader extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.sticky || 'always';
      this.transparent = this.hasAttribute('data-transparent');
      this.lastY = 0;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      const h = this.offsetHeight;
      if (h) document.documentElement.style.setProperty('--header-height', h + 'px');
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }
    onScroll() {
      const y = window.scrollY;
      this.classList.toggle('is-stuck', y > 4);
      if (this.transparent) this.classList.toggle('is-transparent', y <= 4);
      if (this.mode === 'scroll-up') {
        const goingDown = y > this.lastY && y > 140;
        this.classList.toggle('is-hidden', goingDown);
      }
      this.lastY = y;
    }
  }
  customElements.define('lv-header', LvHeader);

  /* --- <lv-quantity> --------------------------------------------------------------------------- */
  class LvQuantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-plus]')) this.step(1);
        if (e.target.closest('[data-minus]')) this.step(-1);
      });
    }
    step(dir) {
      if (!this.input) return;
      const min = parseInt(this.input.min || '1', 10);
      const max = parseInt(this.input.max || '9999', 10);
      const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || min) + dir));
      this.input.value = next;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  customElements.define('lv-quantity', LvQuantity);

  /* --- Panier (API + drawer) ---------------------------------------------------------------------- */
  L.Cart = {
    async getState() {
      const res = await fetch(`${L.routes.cart || '/cart'}.js`);
      return res.json();
    },

    async add(formDataOrItems) {
      let options;
      if (formDataOrItems instanceof FormData) {
        options = { method: 'POST', body: formDataOrItems };
      } else {
        options = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: formDataOrItems }),
        };
      }
      const res = await fetch(`${L.routes.cartAdd || '/cart/add'}.js`, options);
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || L.strings.error);
      await this.refresh();
      return data;
    },

    async change(key, quantity) {
      const res = await fetch(`${L.routes.cartChange || '/cart/change'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || L.strings.cartError);
      await this.refresh(data);
      return data;
    },

    async refresh(cartData) {
      const cart = cartData || (await this.getState());
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.toggleAttribute('hidden', cart.item_count === 0);
      });
      // Re-rendu du drawer via la Section Rendering API
      const drawerContent = document.querySelector('[data-cart-drawer-content]');
      if (drawerContent) {
        try {
          const res = await fetch(`${L.routes.root || '/'}?sections=cart-drawer`);
          const sections = await res.json();
          if (sections['cart-drawer']) {
            const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
            const fresh = doc.querySelector('[data-cart-drawer-content]');
            if (fresh) drawerContent.replaceWith(fresh);
          }
        } catch (e) {
          /* silencieux : le compteur reste juste */
        }
      }
      document.dispatchEvent(new CustomEvent('lyvora:cart:updated', { detail: { cart } }));
      return cart;
    },

    openDrawer() {
      const drawer = document.getElementById('CartDrawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    },
  };

  /* --- <lv-cart-form> : ajout AJAX -------------------------------------------------------------------- */
  class LvCartForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', async (e) => {
        if (this.form.action.indexOf('/cart/add') === -1) return;
        e.preventDefault();
        const btn = this.form.querySelector('[data-atc]') || this.form.querySelector('[type="submit"]');
        if (btn) {
          btn.classList.add('is-loading');
          if (!btn.querySelector('.lv-spinner')) {
            const sp = document.createElement('span');
            sp.className = 'lv-spinner';
            btn.appendChild(sp);
          }
        }
        try {
          // Add-ons cochés (data-addon-id) : ajout multi-articles en un appel
          // form.id est masqué par l'input name="id" (variante) : lire l'attribut
          const formAttrId = this.form.getAttribute('id');
          const addons = [...this.form.querySelectorAll('input[data-addon-id]:checked')]
            .concat(formAttrId ? [...document.querySelectorAll(`input[data-addon-id][form="${CSS.escape(formAttrId)}"]:checked`)] : [])
            .filter((el, i, arr) => arr.indexOf(el) === i);
          if (addons.length) {
            const fd = new FormData(this.form);
            const items = [
              {
                id: parseInt(fd.get('id'), 10),
                quantity: parseInt(fd.get('quantity') || '1', 10) || 1,
                selling_plan: fd.get('selling_plan') || undefined,
              },
            ];
            addons.forEach((a) => items.push({ id: parseInt(a.dataset.addonId, 10), quantity: 1 }));
            await L.Cart.add(items);
          } else {
            await L.Cart.add(new FormData(this.form));
          }
          if (L.settings.cartType === 'drawer') {
            L.Cart.openDrawer();
          } else {
            L.Toast.show(L.strings.addedToCart, 'success');
          }
          this.dispatchEvent(new CustomEvent('lyvora:product:added', { bubbles: true }));
        } catch (err) {
          L.Toast.show(err.message || L.strings.error, 'error');
        } finally {
          if (btn) btn.classList.remove('is-loading');
        }
      });
    }
  }
  customElements.define('lv-cart-form', LvCartForm);

  /* --- <lv-cart-items> : lignes du panier (drawer & page) ------------------------------------------------ */
  class LvCartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', async (e) => {
        const input = e.target.closest('[data-line-qty]');
        if (!input) return;
        this.update(input.dataset.lineQty, parseInt(input.value, 10) || 0);
      });
      this.addEventListener('click', async (e) => {
        const removeBtn = e.target.closest('[data-line-remove]');
        if (!removeBtn) return;
        e.preventDefault();
        this.update(removeBtn.dataset.lineRemove, 0);
      });
    }
    async update(key, qty) {
      this.style.opacity = '0.55';
      this.style.pointerEvents = 'none';
      try {
        await L.Cart.change(key, qty);
        // Sur la page panier (rendue côté serveur), on recharge pour des totaux exacts.
        if (document.body.classList.contains('template-cart') && !this.closest('[data-cart-drawer-content]')) {
          window.location.reload();
          return;
        }
        this.style.opacity = '';
        this.style.pointerEvents = '';
      } catch (err) {
        L.Toast.show(err.message, 'error');
        this.style.opacity = '';
        this.style.pointerEvents = '';
      }
    }
  }
  customElements.define('lv-cart-items', LvCartItems);

  /* --- <lv-free-shipping> --------------------------------------------------------------------------------- */
  class LvFreeShipping extends HTMLElement {
    connectedCallback() {
      this.threshold = parseInt(this.dataset.threshold || L.settings.freeShippingThreshold || '0', 10);
      this.msgEl = this.querySelector('[data-fs-message]');
      this.barEl = this.querySelector('[data-fs-bar]');
      this.onUpdate = (e) => this.render(e.detail.cart.total_price);
      document.addEventListener('lyvora:cart:updated', this.onUpdate);
      const initial = parseInt(this.dataset.total || '-1', 10);
      if (initial >= 0) this.render(initial);
    }
    disconnectedCallback() {
      document.removeEventListener('lyvora:cart:updated', this.onUpdate);
    }
    render(total) {
      if (!this.threshold) return;
      const pct = Math.min(100, Math.round((total / this.threshold) * 100));
      if (this.barEl) this.barEl.style.setProperty('--progress', pct + '%');
      if (this.msgEl) {
        if (total >= this.threshold) {
          this.msgEl.textContent = L.strings.freeShippingReached;
        } else {
          this.msgEl.textContent = (L.strings.freeShippingRemaining || '[amount]').replace(
            '[amount]',
            L.formatMoney(this.threshold - total)
          );
        }
      }
    }
  }
  customElements.define('lv-free-shipping', LvFreeShipping);

  /* --- <lv-before-after> ------------------------------------------------------------------------------------ */
  class LvBeforeAfter extends HTMLElement {
    connectedCallback() {
      this.handle = this.querySelector('.lv-ba__handle');
      this.afterEl = this.querySelector('.lv-ba__after');
      if (!this.handle || !this.afterEl) return;
      this.set(50);
      const move = (clientX) => {
        const rect = this.getBoundingClientRect();
        const pct = Math.min(98, Math.max(2, ((clientX - rect.left) / rect.width) * 100));
        this.set(pct);
      };
      this.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        this.setPointerCapture(e.pointerId);
        move(e.clientX);
        this.dragging = true;
      });
      this.addEventListener('pointermove', (e) => {
        if (this.dragging) move(e.clientX);
      });
      this.addEventListener('pointerup', () => (this.dragging = false));
      this.addEventListener('pointercancel', () => (this.dragging = false));
      this.handle.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.set(this.pct - 4);
        if (e.key === 'ArrowRight') this.set(this.pct + 4);
      });
    }
    set(pct) {
      this.pct = Math.min(98, Math.max(2, pct));
      this.style.setProperty('--ba-pos', this.pct + '%');
      if (this.handle) this.handle.setAttribute('aria-valuenow', Math.round(this.pct));
    }
  }
  customElements.define('lv-before-after', LvBeforeAfter);

  /* --- <lv-video-lite> : lecture vidéo différée (façade poster) -------------------------------------------------- */
  class LvVideoLite extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-video-play]')) this.play();
      });
    }
    play() {
      const tpl = this.querySelector('template');
      if (!tpl || this.dataset.playing) return;
      this.dataset.playing = 'true';
      const poster = this.querySelector('[data-video-poster]');
      const btn = this.querySelector('[data-video-play]');
      if (poster) poster.remove();
      if (btn) btn.remove();
      this.appendChild(tpl.content.cloneNode(true));
      const video = this.querySelector('video');
      if (video) video.play().catch(() => {});
    }
  }
  customElements.define('lv-video-lite', LvVideoLite);

  /* --- <lv-discount-form> : code promo AJAX ----------------------------------------------------------------------- */
  class LvDiscountForm extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="text"], input[type="search"]');
      this.msg = this.querySelector('[data-discount-msg]');
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-discount-apply]')) {
          e.preventDefault();
          this.apply();
        }
        const removeBtn = e.target.closest('[data-discount-remove]');
        if (removeBtn) {
          e.preventDefault();
          this.update('', removeBtn.dataset.discountRemove);
        }
      });
      if (this.input) {
        this.input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.apply();
          }
        });
      }
    }
    async apply() {
      const code = this.input && this.input.value.trim();
      if (!code) return;
      this.update(code);
    }
    async update(code, removeCode) {
      const btn = this.querySelector('[data-discount-apply]');
      if (btn) btn.classList.add('is-loading');
      try {
        let codes = code;
        const state = await L.Cart.getState();
        const existing = (state.discount_codes || []).map((d) => d.code);
        if (removeCode) {
          codes = existing.filter((c) => c !== removeCode).join(',');
        } else if (existing.length) {
          codes = existing.concat(code).join(',');
        }
        const res = await fetch(`${L.routes.cartUpdate || '/cart/update'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: codes }),
        });
        const cart = await res.json();
        if (!res.ok) throw new Error(cart.description || L.strings.error);
        const applied = (cart.discount_codes || []).find((d) => d.code.toLowerCase() === (code || '').toLowerCase());
        if (code && (!applied || !applied.applicable)) {
          if (this.msg) {
            this.msg.hidden = false;
            this.msg.textContent = this.dataset.errorText || L.strings.error;
          }
          // retire le code invalide
          const valid = (cart.discount_codes || []).filter((d) => d.applicable).map((d) => d.code).join(',');
          await fetch(`${L.routes.cartUpdate || '/cart/update'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ discount: valid }),
          });
        } else {
          if (this.msg) this.msg.hidden = true;
          if (this.input) this.input.value = '';
        }
        await L.Cart.refresh();
      } catch (err) {
        if (this.msg) {
          this.msg.hidden = false;
          this.msg.textContent = err.message || L.strings.error;
        }
      } finally {
        if (btn) btn.classList.remove('is-loading');
      }
    }
  }
  customElements.define('lv-discount-form', LvDiscountForm);

  /* --- <lv-cart-note> : note de commande --------------------------------------------------------------------------- */
  class LvCartNote extends HTMLElement {
    connectedCallback() {
      const area = this.querySelector('textarea');
      if (!area) return;
      area.addEventListener(
        'input',
        debounce(() => {
          fetch(`${L.routes.cartUpdate || '/cart/update'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: area.value }),
          }).catch(() => {});
        }, 500)
      );
    }
  }
  customElements.define('lv-cart-note', LvCartNote);

  /* --- <lv-card-media> : mini-galerie des cartes produit (flèches au survol) --- */
  class LvCardMedia extends HTMLElement {
    connectedCallback() {
      this.imgs = [...this.querySelectorAll('[data-cm-img]')];
      if (this.imgs.length < 2) return;
      this.index = 0;
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-cm-prev], [data-cm-next]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        this.show(this.index + (btn.hasAttribute('data-cm-next') ? 1 : -1));
      });
    }
    show(n) {
      this.index = (n + this.imgs.length) % this.imgs.length;
      this.imgs.forEach((img, i) => img.classList.toggle('is-current', i === this.index && i !== 0));
      // La photo principale (index 0) reste visible sous les calques : on la masque si une autre est active
      this.imgs[0].style.opacity = this.index === 0 ? '' : '0';
    }
  }
  customElements.define('lv-card-media', LvCardMedia);

  /* --- Wishlist (localStorage, sans app) ----------------------------------------------------------------------- */
  const WISHLIST_KEY = 'lyvora:wishlist';
  L.Wishlist = {
    all() {
      try {
        return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
      } catch (e) {
        return [];
      }
    },
    has(handle) {
      return this.all().includes(handle);
    },
    toggle(handle) {
      let items = this.all();
      const active = items.includes(handle);
      items = active ? items.filter((h) => h !== handle) : [...items, handle];
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
      document.dispatchEvent(new CustomEvent('lyvora:wishlist:updated', { detail: { items } }));
      return !active;
    },
    syncCounts() {
      const count = this.all().length;
      document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
        el.textContent = count;
        el.toggleAttribute('hidden', count === 0);
      });
    },
  };

  class LvWishlistButton extends HTMLElement {
    connectedCallback() {
      if (!L.settings.wishlist) {
        this.hidden = true;
        return;
      }
      this.btn = this.querySelector('button') || this;
      this.sync();
      this.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const added = L.Wishlist.toggle(this.dataset.handle);
        if (added) L.Toast.show(L.strings.wishlistAdded, 'success', 1800);
        this.sync();
      });
      document.addEventListener('lyvora:wishlist:updated', () => this.sync());
    }
    sync() {
      const active = L.Wishlist.has(this.dataset.handle);
      this.btn.classList.toggle('is-active', active);
      this.btn.setAttribute('aria-label', active ? L.strings.wishlistRemove : L.strings.wishlistAdd);
      this.btn.setAttribute('aria-pressed', active);
    }
  }
  customElements.define('lv-wishlist-button', LvWishlistButton);
  onReady(() => L.Wishlist.syncCounts());
  document.addEventListener('lyvora:wishlist:updated', () => L.Wishlist.syncCounts());

  /* --- <lv-wishlist-grid> : rend les produits de la wishlist --------------------------------------------------------- */
  class LvWishlistGrid extends HTMLElement {
    connectedCallback() {
      this.render();
      document.addEventListener('lyvora:wishlist:updated', () => this.render());
    }
    async render() {
      const handles = L.Wishlist.all();
      const empty = this.querySelector('[data-wishlist-empty]');
      const grid = this.querySelector('[data-wishlist-items]');
      if (!grid) return;
      if (!handles.length) {
        grid.innerHTML = '';
        if (empty) empty.hidden = false;
        return;
      }
      if (empty) empty.hidden = true;
      const cards = await Promise.all(
        handles.map(async (handle) => {
          try {
            const root = (L.routes.root || '/').replace(/\/?$/, '/');
            const res = await fetch(`${root}products/${handle}?section_id=wishlist-product-card`);
            if (!res.ok) return '';
            const html = await res.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const card = doc.querySelector('[data-wishlist-card]');
            return card ? card.outerHTML : '';
          } catch (e) {
            return '';
          }
        })
      );
      grid.innerHTML = cards.join('');
    }
  }
  customElements.define('lv-wishlist-grid', LvWishlistGrid);

  /* --- <lv-predictive-search> ------------------------------------------------------------------------------------------ */
  class LvPredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-search-results]');
      if (!this.input || !this.results) return;
      this.input.addEventListener(
        'input',
        debounce(() => this.search(), 280)
      );
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.results.innerHTML = '';
        return;
      }
      this.results.innerHTML = '<div class="lv-skeleton" style="height:120px"></div>';
      try {
        const url = `${L.routes.predictiveSearch || '/search/suggest'}?q=${encodeURIComponent(
          q
        )}&resources[type]=product,collection,page,article&resources[limit]=6&section_id=predictive-search`;
        const res = await fetch(url);
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const inner = doc.querySelector('[data-predictive-results]');
        this.results.innerHTML = inner ? inner.innerHTML : '';
      } catch (e) {
        this.results.innerHTML = '';
      }
    }
  }
  customElements.define('lv-predictive-search', LvPredictiveSearch);

  /* --- Localisation (langue / pays) -------------------------------------------------------------------------------------- */
  onReady(() => {
    document.querySelectorAll('[data-localization-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const form = select.closest('form');
        if (form) form.submit();
      });
    });
  });
})();
